import { API_BASE_URL, ADMIN_API_BASE_URL } from '../config';

/**
 * Admin API client (Brands & Celebrities).
 * Connects using ADMIN_API_BASE_URL or configured REACT_APP_ADMIN_API_BASE_URL.
 */

export const getAdminBaseUrl = () => {
  if (process.env.REACT_APP_ADMIN_API_BASE_URL) {
    return process.env.REACT_APP_ADMIN_API_BASE_URL.replace(/\/+$/, '');
  }
  if (ADMIN_API_BASE_URL) {
    return ADMIN_API_BASE_URL.replace(/\/+$/, '');
  }
  return (API_BASE_URL || '').replace(/\/+$/, '') || 'https://api-admin.polopan.com';
};

export const ADMIN_BASE_URL = getAdminBaseUrl();

async function request(path, options = {}) {
  const baseUrl = getAdminBaseUrl();
  const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
  const authToken = localStorage.getItem('authToken') || localStorage.getItem('authKey') || '';
  const authKey = localStorage.getItem('authKey') || '';
  const userId = localStorage.getItem('userId') || '';
  const userEmail = localStorage.getItem('userEmail') || '';

  const headers = {
    Accept: 'application/json',
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    ...(authKey ? { 'X-Auth-Key': authKey } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
    ...(userEmail ? { 'X-User-Email': userEmail } : {}),
    ...(options.headers || {}),
  };

  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers,
  });



  const contentType = res.headers.get('content-type') || '';
  let data;
  if (contentType.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    const errorMsg = (data && data.detail) || (data && data.message) || (typeof data === 'string' ? data : res.statusText);
    const error = new Error(errorMsg || `Request failed with status ${res.status}`);
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const adminApi = {
  async getMe() {
    return request('/auth/me', { skipRedirect: true });
  },

  async getCelebritiesMeta() {
    return request('/api/celebrities/meta');
  },

  async getCelebrities(params = {}) {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.gender) query.set('gender', params.gender);
    if (params.limit) query.set('limit', params.limit);
    if (params.skip) query.set('skip', params.skip);
    const qs = query.toString();
    return request(`/api/celebrities${qs ? `?${qs}` : ''}`);
  },

  async getCelebrity(id) {
    return request(`/api/celebrities/${id}`);
  },

  async createCelebrity(payload) {
    return request('/api/celebrities', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateCelebrity(id, payload) {
    return request(`/api/celebrities/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  async deleteCelebrity(id) {
    return request(`/api/celebrities/${id}`, {
      method: 'DELETE',
    });
  },

  async uploadCelebrityImages(id, files) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return request(`/api/celebrities/${id}/images`, {
      method: 'POST',
      body: formData,
    });
  },

  async reorderCelebrityImages(id, filenames) {
    return request(`/api/celebrities/${id}/images/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ filenames }),
    });
  },

  async deleteCelebrityImage(id, filename) {
    return request(`/api/celebrities/${id}/images/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
  },

  async regenerateCelebrityEmbeddings(id) {
    return request(`/api/celebrities/${id}/regenerate-embeddings`, {
      method: 'POST',
    });
  },

  async getCelebrityEmbedJobs(id) {
    return request(`/api/celebrities/${id}/embed-jobs`);
  },

  async getCustomFields() {
    return request('/api/custom-fields');
  },

  async registerCustomField(key) {
    return request('/api/custom-fields', {
      method: 'POST',
      body: JSON.stringify({ key }),
    });
  },

  async getBrands(params = {}) {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.partnership !== undefined && params.partnership !== null && params.partnership !== '') {
      query.set('partnership', params.partnership);
    }
    if (params.limit) query.set('limit', params.limit || 500);
    if (params.skip) query.set('skip', params.skip || 0);
    const qs = query.toString();
    return request(`/api/brands${qs ? `?${qs}` : ''}`);
  },

  async getBrand(id) {
    return request(`/api/brands/${id}`);
  },

  async createBrand(payload) {
    return request('/api/brands', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async updateBrand(id, payload) {
    return request(`/api/brands/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  async deleteBrand(id) {
    return request(`/api/brands/${id}`, {
      method: 'DELETE',
    });
  },

  async toggleBrandActive(id, isActive) {
    return request(`/api/brands/${id}/toggle-active`, {
      method: 'POST',
      body: JSON.stringify(isActive !== undefined ? { is_active: isActive } : {}),
    });
  },

  async getBrandProducts(brandId, params = {}) {
    const query = new URLSearchParams();
    if (params.gender && params.gender !== 'all') query.set('gender', params.gender);
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    if (params.limit) query.set('limit', params.limit);
    if (params.skip) query.set('skip', params.skip);
    const qs = query.toString();
    return request(`/api/brands/${brandId}/products${qs ? `?${qs}` : ''}`);
  },

  async toggleProductStatus(productId, data = {}) {
    return request(`/api/products/${productId}/toggle-status`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateProductGender(productId, gender) {
    return request(`/api/products/${productId}/gender`, {
      method: 'POST',
      body: JSON.stringify({ gender }),
    });
  },

  async getOrders(params = {}) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    const qs = query.toString();
    return request(`/api/orders${qs ? `?${qs}` : ''}`);
  },

  async getProductCategoryTaxonomy() {
    return request('/api/admin/products/categories/taxonomy');
  },

  async getProductCategoryTree(params = {}) {
    const query = new URLSearchParams();
    if (params.status && params.status !== 'all') query.set('status', params.status);
    const qs = query.toString();
    return request(`/api/admin/products/categories/tree${qs ? `?${qs}` : ''}`);
  },

  async getAdminProducts(params = {}) {
    const query = new URLSearchParams();
    if (params.super_category && params.super_category !== 'all') query.set('super_category', params.super_category);
    if (params.category && params.category !== 'all') query.set('category', params.category);
    if (params.gender && params.gender !== 'all') query.set('gender', params.gender);
    if (params.vendor && params.vendor !== 'all') query.set('vendor', params.vendor);
    if (params.is_ethnic !== undefined && params.is_ethnic !== null && params.is_ethnic !== 'all') {
      query.set('is_ethnic', params.is_ethnic);
    }
    if (params.search) query.set('search', params.search);
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.page) query.set('page', params.page);
    if (params.limit) query.set('limit', params.limit);
    const qs = query.toString();
    return request(`/api/admin/products${qs ? `?${qs}` : ''}`);
  },

  async updateProductCategory(productId, payload) {
    return request(`/api/admin/products/${productId}/category`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  async bulkUpdateProductCategory(payload) {
    return request('/api/admin/products/bulk-category', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};

